import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stepper,
  Stack,
  Group,
  Title,
  Text,
  ActionIcon,
  Box,
  useMantineColorScheme,
  LoadingOverlay,
  Progress
} from '@mantine/core';
import {
  IconArrowLeft,
  IconSun,
  IconMoon,
  IconCheck,
  IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import PageTransition from '../animations/PageTransition';
import Step1PersonalData from './Step1PersonalData';
import Step2Contact from './Step2Contact';
import Step3EmailVerification from './Step3EmailVerification';
// ❌ Step4PhoneVerification eliminado - el teléfono ya no se verifica
// ❌ Step5PlanSelection eliminado - se crea cuenta FREE automáticamente

import {
  RegistroWizardData,
  WIZARD_STEPS,
  Paso1DatosPersonales,
  Paso2Contacto
} from '../../types/registro';
import registroTemporalService from '../../services/registroTemporalService';
import { registroService } from '../../services/registroService';

const RegistrationWizard: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  
  const [loading, setLoading] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [wizardData, setWizardData] = useState<RegistroWizardData>({
    currentStep: 0,
    totalSteps: WIZARD_STEPS.length,
    steps: WIZARD_STEPS.map((step, index) => ({
      ...step,
      isCompleted: false,
      isActive: index === 0,
      isAccessible: index === 0
    }))
  });

  // Inicializar sesión temporal al montar el componente
  React.useEffect(() => {
    const initializeSession = async () => {
      try {
        // Intentar restaurar sesión existente primero
        const hasExistingSession = registroTemporalService.restaurarSesionTemporal();
        
        if (!hasExistingSession) {
          // Crear nueva sesión temporal si no existe
          await registroTemporalService.iniciarSesionTemporal();
        }
        
        setSessionInitialized(true);
      } catch (error) {
        console.error('Error initializing temporal session:', error);
        // Continuar sin sesión temporal (fallback al método original)
        setSessionInitialized(true);
      }
    };

    initializeSession();
  }, []);

  // ==========================================================
  // NAVEGACIÓN ENTRE PASOS
  // ==========================================================

  const nextStep = () => {
    if (wizardData.currentStep < wizardData.totalSteps - 1) {
      const newCurrentStep = wizardData.currentStep + 1;
      
      setWizardData(prev => ({
        ...prev,
        currentStep: newCurrentStep,
        steps: prev.steps.map((step, index) => ({
          ...step,
          isCompleted: index < newCurrentStep,
          isActive: index === newCurrentStep,
          isAccessible: index <= newCurrentStep
        }))
      }));
    }
  };

  const prevStep = () => {
    if (wizardData.currentStep > 0) {
      const newCurrentStep = wizardData.currentStep - 1;
      
      setWizardData(prev => ({
        ...prev,
        currentStep: newCurrentStep,
        steps: prev.steps.map((step, index) => ({
          ...step,
          isActive: index === newCurrentStep,
          isCompleted: index < newCurrentStep
        }))
      }));
    }
  };

  const goToStep = (stepIndex: number) => {
    if (wizardData.steps[stepIndex]?.isAccessible) {
      setWizardData(prev => ({
        ...prev,
        currentStep: stepIndex,
        steps: prev.steps.map((step, index) => ({
          ...step,
          isActive: index === stepIndex,
          isCompleted: index < stepIndex
        }))
      }));
    }
  };

  // ==========================================================
  // MANEJO DE DATOS DE PASOS
  // ==========================================================

  const handlePaso1Complete = (data: Paso1DatosPersonales) => {
    setWizardData(prev => ({
      ...prev,
      paso1: data
    }));
    
    // Guardar credenciales temporalmente para auto-login después del registro
    // Solo username y contraseña para el login, expira al cerrar pestaña
    try {
      sessionStorage.setItem('registro_temp_username', data.username);
      sessionStorage.setItem('registro_temp_password', data.contrasena);
    } catch (e) {
      console.warn('No se pudieron guardar credenciales temporales:', e);
    }
    
    nextStep();
  };

  const handlePaso3Progress = (codigo: string) => {
    setWizardData(prev => ({
      ...prev,
      emailCodigo: codigo
    }));
  };

  const handlePaso2Complete = (data: Paso2Contacto, sessionToken: string) => {
    // Detectar modo de operación
    const isLegacyMode = sessionToken.startsWith('LEGACY:');
    const isWizardOnly = sessionToken === 'WIZARD_ONLY';
    const usuarioId = isLegacyMode ? parseInt(sessionToken.replace('LEGACY:', '')) : 0;
    
    setWizardData(prev => ({
      ...prev,
      paso2: data,
      usuarioTemporal: (!isLegacyMode && !isWizardOnly) ? {
        sessionToken: sessionToken,
        email: data.email,
        telefono: data.telefono
      } : undefined,
      // Para compatibilidad con flujo legado
      usuarioCreado: isLegacyMode ? {
        id: usuarioId,
        email: data.email,
        telefono: data.telefono
      } : undefined
    }));
    nextStep();
  };

  const handleEmailVerified = async () => {
    // ✅ Al verificar email, automáticamente se crea la cuenta y se hace login
    try {
      setLoading(true);
      const { authService } = await import('../../services/authService');

      let username: string | null = null;
      let contrasena: string | null = null;

      // 1. Intentar obtener credenciales de la memoria React primero
      if (wizardData.paso1?.username && wizardData.paso1?.contrasena) {
        username = wizardData.paso1.username;
        contrasena = wizardData.paso1.contrasena;
        console.log('✅ Credenciales obtenidas de memoria React');
      }
      // 2. Fallback: obtener de sessionStorage si se perdió el estado React
      else {
        try {
          username = sessionStorage.getItem('registro_temp_username');
          contrasena = sessionStorage.getItem('registro_temp_password');

          if (username && contrasena) {
            console.log('✅ Credenciales recuperadas de sessionStorage');
          } else {
            console.error('❌ No hay credenciales en sessionStorage');
            navigate('/login');
            return;
          }
        } catch (e) {
          console.error('Error accediendo a sessionStorage:', e);
          navigate('/login');
          return;
        }
      }

      // 3. Intentar login automático
      try {
        await authService.login({
          login: username,
          password: contrasena
        });

        console.log('✅ Auto-login exitoso - Registro completado');

        // Limpiar datos temporales
        registroTemporalService.limpiarSesionTemporal();
        sessionStorage.removeItem('registro_temp_username');
        sessionStorage.removeItem('registro_temp_password');

        // Redirigir a mejorar plan
        navigate('/mejorarplan');
      } catch (loginError) {
        console.error('❌ Error en auto-login:', loginError);

        // Limpiar credenciales temporales
        sessionStorage.removeItem('registro_temp_username');
        sessionStorage.removeItem('registro_temp_password');

        // Redirigir a login manual
        navigate('/login');
      }
    } catch (error) {
      console.error('Error en handleEmailVerified:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  // ❌ handlePhoneVerified ELIMINADO - el teléfono ya no se verifica

  // ==========================================================
  // RENDERIZADO DE PASOS
  // ==========================================================

  const renderCurrentStep = () => {
    switch (wizardData.currentStep) {
      case 0:
        return (
          <Step1PersonalData
            onComplete={handlePaso1Complete}
            loading={loading}
            setLoading={setLoading}
            initialData={wizardData.paso1}
          />
        );
      case 1:
        return (
          <Step2Contact
            paso1Data={wizardData.paso1!}
            onComplete={handlePaso2Complete}
            onBack={prevStep}
            loading={loading}
            setLoading={setLoading}
          />
        );
      case 2:
        return (
          <Step3EmailVerification
            email={wizardData.usuarioTemporal?.email || wizardData.usuarioCreado?.email || wizardData.paso2?.email || ''}
            onVerified={handleEmailVerified}
            onBack={prevStep}
            loading={loading}
            setLoading={setLoading}
            initialCodigo={wizardData.emailCodigo}
            onCodigoChange={handlePaso3Progress}
            useTemporalService={!!wizardData.usuarioTemporal}
          />
        );
      // ❌ Step 4 (Verificar Teléfono) ELIMINADO - el teléfono ya no se verifica
      // ❌ Step 5 (Elegir Plan) ELIMINADO - se crea cuenta FREE automáticamente
      default:
        return null;
    }
  };

  const progressPercentage = ((wizardData.currentStep + 1) / wizardData.totalSteps) * 100;

  return (
    <PageTransition type="medical" duration={800}>
      <Box
        style={{
          minHeight: '100vh',
          background: colorScheme === 'dark'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Textura de fondo - patrón de cuadrados */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: colorScheme === 'dark'
              ? `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23374151" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`
              : `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23ddd8d1" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`,
            opacity: 0.5,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* Header con controles */}
        <Box p="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group justify="space-between">
            {/* Botón volver */}
            <ActionIcon
              onClick={() => wizardData.currentStep === 0 ? navigate('/') : prevStep()}
              variant="light"
              size="lg"
              radius="xl"
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px) saturate(180%)'
              }}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>

            {/* Espaciador */}
            <Box style={{ flex: 1 }} />

            {/* Dark mode toggle */}
            <ActionIcon
              onClick={toggleColorScheme}
              variant="light"
              size="lg"
              radius="xl"
              disabled={true}
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px) saturate(180%)',
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
            >
              {colorScheme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>
          </Group>
        </Box>

        {/* Contenido principal */}
        <Container size="md" px="md" pb="xl" style={{ position: 'relative', zIndex: 1 }}>
          <Paper
            radius="xl"
            p="xl"
            withBorder
            style={{
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.6)',
              backdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
              position: 'relative'
            }}
          >
            <LoadingOverlay visible={loading} />

            {/* Header del paso actual */}
            <Stack gap="lg" mb="xl">
              <Box ta="center">
                <Title order={2} mb="xs">
                  {WIZARD_STEPS[wizardData.currentStep]?.title}
                </Title>
                <Text c="dimmed">
                  {WIZARD_STEPS[wizardData.currentStep]?.description}
                </Text>
              </Box>

              {/* Stepper visual */}
              <Stepper 
                active={wizardData.currentStep} 
                orientation="horizontal"
                size="sm"
                allowNextStepsSelect={false}
                styles={{
                  step: {
                    cursor: 'default'
                  }
                }}
              >
                {WIZARD_STEPS.map((step, index) => (
                  <Stepper.Step
                    key={step.id}
                    label={step.title}
                    allowStepClick={wizardData.steps[index]?.isAccessible}
                    onClick={() => goToStep(index)}
                  />
                ))}
              </Stepper>
            </Stack>

            {/* Contenido del paso actual */}
            <Box>
              {renderCurrentStep()}
            </Box>
          </Paper>
        </Container>
      </Box>
    </PageTransition>
  );
};

export default RegistrationWizard;